import React, { FC, useMemo } from "react";

import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { useGetDailyTopPicks } from "@api";

import { InfoBox } from "@ui/components";
import { Box, Button, Icon, Text, Title } from "@ui/elements";
import { Stack } from "@ui/layout";

import { Tooltip, TooltipContent, TooltipTrigger } from "@components";

import { usePageSettings } from "../PageTemplate/PageTemplate";

import { LockedTipBox } from "./LockedTipBox";
import { TopPickBoxSkeleton } from "./TipBoxSkeleton";
import { TopPickBox } from "./TopPickBox";
import "./DailyTopPicks.scss";

type DailyTopPickProps = {
  date: string;
};

export const DailyTopPicks: FC<DailyTopPickProps> = ({ date }) => {
  const { t } = useTranslation();
  const { data: dailyTopPicksData, isLoading: dailyTopPicksLoading } =
    useGetDailyTopPicks({
      from: dayjs(date).format("YYYY-MM-DD"),
      to: dayjs(date).format("YYYY-MM-DD"),
    });
  const settings = usePageSettings();

  const safePicks = useMemo(() => {
    return (
      dailyTopPicksData?.data
        ?.filter((pick) => pick.tip.type === "safe")
        .sort((a, b) => a.tip.rank - b.tip.rank) || []
    );
  }, [dailyTopPicksData]);

  const valuePicks = useMemo(() => {
    return (
      dailyTopPicksData?.data
        ?.filter((pick) => pick.tip.type === "value")
        .sort((a, b) => a.tip.rank - b.tip.rank) || []
    );
  }, [dailyTopPicksData]);

  if (
    dailyTopPicksData &&
    dailyTopPicksData.meta.total === 0 &&
    !dailyTopPicksLoading
  ) {
    return (
      <InfoBox hasSpacing title={t("noTopPicksAvailableTitle")}>
        <Text
          dangerouslySetInnerHTML={{ __html: t("noTopPicksAvailableInfo") }}
        />
      </InfoBox>
    );
  }

  return (
    <Stack direction="row" gap="xl" className="daily-top-picks-columns">
      <Stack direction="column" isFullwidth>
        <Title size={5} hasIcon isBlock>
          <Stack align="center" gap="xxs" justify="between">
            <Stack align="center" gap="xs">
              <Icon icon="ShieldCheck" size="medium" />
              {t("safePicks")}
            </Stack>
            <Tooltip placement="bottom-end">
              <TooltipTrigger>
                <Button
                  variant="ghost"
                  className="has-text-weight-normal"
                  size="small"
                >
                  <Icon icon="Info" size="small" />
                  <span>{t("whats-this")}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <Box className="tooltip-box">
                  <Text>{t("safePicksInfo")}</Text>
                </Box>
              </TooltipContent>
            </Tooltip>
          </Stack>
        </Title>
        {!settings.hasSubscription &&
        !dailyTopPicksLoading &&
        dailyTopPicksData ? (
          <Stack direction="column" isFullwidth gap="lg">
            <LockedTipBox tipsAvailable={dailyTopPicksData.meta.total_safe} />
          </Stack>
        ) : (
          <Stack
            direction="column"
            isFullwidth
            gap="lg"
            className="top-picks-stack"
          >
            {dailyTopPicksLoading && (
              <>
                <TopPickBoxSkeleton />
                <TopPickBoxSkeleton />
                <TopPickBoxSkeleton />
              </>
            )}
            {safePicks.map((pick) => (
              <TopPickBox key={`top-pick-box-${pick.tip.id}`} {...pick} />
            ))}
          </Stack>
        )}
      </Stack>
      <Stack direction="column" isFullwidth>
        <Title size={5} hasIcon isBlock>
          <Stack align="center" gap="xxs" justify="between">
            <Stack align="center" gap="xs">
              <Icon icon="Coins" size="medium" />
              {t("valuePicks")}
            </Stack>
            <Tooltip placement="bottom-end">
              <TooltipTrigger>
                <Button
                  variant="ghost"
                  className="has-text-weight-normal"
                  size="small"
                >
                  <Icon icon="Info" size="small" />
                  <span>{t("whats-this")}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <Box className="tooltip-box">
                  <Text>{t("valuePicksInfo")}</Text>
                </Box>
              </TooltipContent>
            </Tooltip>
          </Stack>
        </Title>
        {!settings.hasSubscription &&
        !dailyTopPicksLoading &&
        dailyTopPicksData ? (
          <Stack direction="column" isFullwidth gap="lg">
            <LockedTipBox tipsAvailable={dailyTopPicksData.meta.total_value} />
          </Stack>
        ) : (
          <Stack
            direction="column"
            isFullwidth
            gap="lg"
            className="top-picks-stack"
          >
            {dailyTopPicksLoading && (
              <>
                <TopPickBoxSkeleton />
                <TopPickBoxSkeleton />
                <TopPickBoxSkeleton />
              </>
            )}
            {valuePicks.map((pick) => (
              <TopPickBox key={`top-pick-box-${pick.tip.id}`} {...pick} />
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
